// @ts-strict-ignore
import React, { useMemo } from 'react'
import i18next from 'i18next'
import { isEmpty, startCase } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { Box, CopyButton, Highlight, Icon, Box as Row, Tooltip } from '@node-space/ui-components'
import { ellipseMiddleText } from '@node-space/utils'
import { SidePanelHeaderTemplateTwo } from 'components/SidePanel/SidePanelHeaderTemplates/SidePanelHeaderTemplateTwo'
import { getTransactionTitle } from 'pages/WalletDetails/hooks/useTitleTransaction'
import { useTransactionHistoryLabel } from 'pages/Wallets/components/Table/SidePanel/hooks/useTransactionHistoryLabel'
import { useTransactionIcons } from 'pages/Wallets/hooks/useTransactionIcons'
import { Transaction } from 'types/transaction'
import { getHighlightType, getSupportedPaymentTypes, titleCase } from 'utils/utils'
import ProofOfPaymentContainer from '../Modal/components/ProofOfPaymentContainer'
import { isTransactionABuySell, isTransactionANetworkOrInternalTransfer } from './utils'

interface SidePanelHeaderWalletProps {
  data: {
    iconUrls: string[]
    amountWithCurrency: string
    transactionReference: string
    transactionType: string
    transactionSubType: string
    currencyFrom?: string
    currencyTo?: string
    status?: string
    externalReference?: string
    hasProofOfPayment?: boolean
    bvnkNetworkBeneficiary?: string
    bvnkNetworkSender?: string
  }
  supportedPaymentTypes: ReturnType<typeof getSupportedPaymentTypes>
}

export const SidePanelHeaderWallet = ({
  data,
  supportedPaymentTypes,
}: SidePanelHeaderWalletProps) => {
  const { t } = useTranslation()
  const {
    iconUrls,
    amountWithCurrency,
    transactionReference,
    transactionType,
    transactionSubType,
    currencyFrom = '',
    currencyTo = '',
    status,
    externalReference,
    hasProofOfPayment,
    bvnkNetworkBeneficiary,
    bvnkNetworkSender,
  } = data

  const transactionsTypeSubType = `${transactionType}_${transactionSubType}`
  const isUserWithdrawal =
    transactionsTypeSubType === supportedPaymentTypes.FIRST_AND_THIRD_PARTY_WITHDRAWAL ||
    transactionsTypeSubType === supportedPaymentTypes.ACCOUNT_VERIFICATION_DIRECT_DEBIT ||
    transactionsTypeSubType === supportedPaymentTypes.FIAT_DIRECT_DEBIT

  const isNetworkOrInternalTransfer = isTransactionANetworkOrInternalTransfer(
    transactionType,
    transactionSubType
  )

  const isBuySell = isTransactionABuySell(transactionType, transactionSubType)

  const transactionAttributes = useMemo(() => {
    const transactionTypeSubType = `${transactionType}_${transactionSubType}`
    const isConvertIn =
      transactionTypeSubType == supportedPaymentTypes.CONVERT_IN ||
      transactionTypeSubType ==
        supportedPaymentTypes.PAYMENT_OUT_ELECTRONIC_MONEY_INTERMEDIARY_TRADE
    const isConvertOut =
      transactionTypeSubType == supportedPaymentTypes.CONVERT_OUT ||
      transactionTypeSubType == supportedPaymentTypes.PAYMENT_IN_ELECTRONIC_MONEY_INTERMEDIARY_TRADE
    return {
      isOfTypeTrade: isConvertIn || isConvertOut,
      copyOfTypeTrade:
        (isConvertIn && i18next.t('transactions.sideBar.convertedIn')) ||
        (isConvertOut && i18next.t('transactions.sideBar.convertedOut')),
    }
  }, [transactionType, transactionSubType])

  const isTransferPayout =
    transactionsTypeSubType === supportedPaymentTypes.BVNK_NETWORK_TRANSFER_PAYMENT_OUT ||
    transactionsTypeSubType === supportedPaymentTypes.INTERNAL_TRANSFER_PAYMENT_OUT

  const internalTranferSecondaryLabel = useTransactionHistoryLabel({
    transactionType,
    transactionSubType,
    name: isTransferPayout ? bvnkNetworkBeneficiary : bvnkNetworkSender,
    fallback: startCase(transactionType || transactionSubType),
  })

  const secondaryLabelContent = useMemo(() => {
    if (transactionAttributes?.isOfTypeTrade) {
      return (
        <Box className="md:flex" align="center" gap={4}>
          <p className="text-body-regular text-foreground-subdued">
            {transactionAttributes?.copyOfTypeTrade} {currencyFrom}
          </p>
          <Icon name="ArrowRight" size="small" />
          <p className="text-body-regular text-foreground-subdued">{currencyTo}</p>
        </Box>
      )
    } else if (isNetworkOrInternalTransfer) {
      return internalTranferSecondaryLabel
    } else {
      return getTransactionTitle({
        transaction: {
          type: transactionType,
          context: { subType: transactionSubType },
        } as Transaction,
        t,
      })
    }
  }, [
    transactionAttributes?.isOfTypeTrade,
    transactionAttributes?.copyOfTypeTrade,
    transactionsTypeSubType,
    bvnkNetworkBeneficiary,
    bvnkNetworkSender,
    currencyFrom,
    currencyTo,
    isNetworkOrInternalTransfer,
    transactionType,
    transactionSubType,
    t,
  ])

  const RightContent = (
    <Row flex className="items-center" gapX={4} paddingB={4}>
      <span className="text-body-regular text-foreground-prominent">
        {ellipseMiddleText(transactionReference, 20, 10)}
      </span>
      <Tooltip bodyContent={t('copyReference')} type="simple">
        <Box flex align="center">
          <CopyButton copyText={transactionReference} />
        </Box>
      </Tooltip>
    </Row>
  )

  const LeftContent = (
    <Highlight type={getHighlightType(status?.toUpperCase())}>{titleCase(status)}</Highlight>
  )

  const { singleIcon, isMultipleIcons } = useTransactionIcons(transactionsTypeSubType, iconUrls)

  return (
    <SidePanelHeaderTemplateTwo
      heading={{
        iconUrls: isMultipleIcons ? iconUrls : singleIcon,
        primaryLabel: transactionAttributes?.isOfTypeTrade ? (
          <p className="text-h2 text-foreground-prominent">{amountWithCurrency}</p>
        ) : (
          amountWithCurrency
        ),
        secondaryLabel: secondaryLabelContent,
      }}
    >
      <Box flexCol gapY={16}>
        <Box className="pl-10">
          {!isEmpty(transactionReference) && RightContent}
          {(isUserWithdrawal || isNetworkOrInternalTransfer || isBuySell) && LeftContent}
        </Box>
        {isUserWithdrawal && (
          <ProofOfPaymentContainer
            transactionType={transactionType}
            transactionSubType={transactionSubType}
            status={status}
            externalReference={externalReference}
            hasProofOfPayment={hasProofOfPayment}
            isLink={true}
            paymentReference={transactionReference}
          />
        )}
      </Box>
    </SidePanelHeaderTemplateTwo>
  )
}
